import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {useMemo} from 'react';
import {uniq} from 'underscore';

import {FormDefinitionField, FormDefinitionSection, FormSectionType} from 'forms/form-designer/Constants';
import {FormInstanceTypedValueField} from 'forms/form-instance/FormInstanceField';
import toColumnConstraint from 'notebook/structured-tables/columns/toColumnConstraint';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, BLUE_POWDER_LIGHT, GRAY_LIGHT, WHITE, GRAY_DARKER} from 'ui-library/Colors';

import {
  FormInstanceComputedTableState,
  FormInstanceTableState,
  FieldKeyToTableColumnIndex,
} from './Constants';

export interface FormInstanceSectionProps {
  definition: FormDefinitionSection;
  autoFocusFirstField?: boolean;
  tableState: FormInstanceTableState;
  computedTableState: FormInstanceComputedTableState;
  updateTableState: (newTableState: FormInstanceTableState) => void;
  fieldKeyToTableColumnIndex: FieldKeyToTableColumnIndex;
  onAddRepeatableSection: (tableKeys: Array<string>) => void;
  onRemoveRepeatableSection: (tableKeys: Array<string>, rowIndex: number) => void;
  /** Text to display if there are no fields in an iterable instance that describes what is being iterated */
  itemName?: string;
}

export interface FormInstanceSectionContentsProps extends FormInstanceSectionProps {
  rowIndex?: number;
}

/** Encapsulates a Section within a Form, either Static or Iterable */
export default function FormInstanceSection(props: FormInstanceSectionProps): JSX.Element | null {
  const {
    definition,
    tableState,
    fieldKeyToTableColumnIndex,
    onAddRepeatableSection,
    onRemoveRepeatableSection,
  } = props;
  const {name, isNameVisible, description, sectionType, content} = definition;
  /** The keys of tables that this Section connects to, generated from what fields it contains */
  let associatedTableKeys = useMemo(() => {
    const fieldsInSection = content.filter(
      (sectionOrField): sectionOrField is FormDefinitionField => sectionOrField.type === 'FormField'
    );
    const tableKeys = fieldsInSection.map((field) => fieldKeyToTableColumnIndex[field.key]?.[0]);
    // dedeuplicate and filter out any missing table keys
    return uniq(tableKeys.filter((tableKey) => tableKey));
  }, [content, fieldKeyToTableColumnIndex]);
  // a stop-gap, but this will do while we only have single-action forms. If there're no associated tables
  // then assume all are relevant
  if (!associatedTableKeys.length) {
    associatedTableKeys = Object.keys(tableState);
  }

  return (
    <div
      className={classNames(css(styles.formSection), {
        [css(styles.staticSection)]: sectionType === FormSectionType.STATIC,
        [css(styles.iterableSection)]: sectionType === FormSectionType.ITERABLE,
      })}
      {...getTestProps({component: 'FormInstanceSection', element: 'root'})}
    >
      {(isNameVisible || description) && (
        <div className={css(styles.sectionText)}>
          {isNameVisible && <h4 className={`form-header ${css(styles.name)}`}>{name}</h4>}
          {description && <p className={css(styles.description)}>{description}</p>}
        </div>
      )}
      {sectionType === FormSectionType.ITERABLE ? (
        <div className={css(styles.iterablesContainer)}>
          {// which table doesn't really matter, we just need to know how many rows this particular
          // iterable section should have so we grab the first associated table
          tableState[associatedTableKeys[0]]?.rows?.map((tableRow, rowIndex, rows) => (
            <div className={css(styles.formSection, styles.staticSection)} key={rowIndex}>
              {rows.length > 1 && (
                <Button
                  icon="delete"
                  className={css(styles.removeIterableButton)}
                  buttonStyle={ButtonStyle.CIRCLE_ICON}
                  size={ButtonSize.MINI}
                  onClick={() => {
                    onRemoveRepeatableSection(associatedTableKeys, rowIndex);
                  }}
                />
              )}
              <FormInstanceSectionContents {...props} rowIndex={rowIndex} />
            </div>
          ))}
          <Button
            icon="compose"
            text="Create another task"
            className={css(styles.addIterableButton)}
            buttonStyle={ButtonStyle.TERTIARY_BLUE_POWDER}
            size={ButtonSize.SMALL}
            onClick={() => {
              onAddRepeatableSection(associatedTableKeys);
            }}
          />
        </div>
      ) : (
        <FormInstanceSectionContents {...props} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  formSection: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '16px 8px 28px',
    marginBottom: '16px',
  },
  staticSection: {
    backgroundColor: WHITE,
  },
  iterableSection: {
    backgroundColor: GRAY_LIGHT,
  },
  iterablesContainer: {
    padding: '0 8px',
  },
  sectionText: {
    gridColumn: 'span 2',
    padding: '0 8px',
  },
  name: {
    gridColumn: 'span 2',
    margin: '0 auto 12px',
    wordWrap: 'break-word',
  },
  description: {
    ...FONT_PROXIMA_13,
  },
  contents: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'flex-end',
    gap: 16,
  },
  addIterableButton: {
    display: 'block',
    margin: '-30px auto 0',
    zIndex: 1,
    backgroundColor: BLUE_POWDER_LIGHT,
    border: `1px solid ${BLUE_LIGHT}`,
    borderRadius: '20px',
  },
  removeIterableButton: {
    display: 'block',
    float: 'right',
  },
  itemName: {
    color: GRAY_DARKER,
    margin: '10px 10px 0',
  },
});

/** The collection of fields within a Static section or an Iterable instance */
function FormInstanceSectionContents(props: FormInstanceSectionContentsProps): JSX.Element {
  const {
    definition,
    autoFocusFirstField,
    tableState,
    computedTableState,
    updateTableState,
    fieldKeyToTableColumnIndex,
    // TODO: BNCH-68084 either a row index is provided or it uses the first row for shared fields, pending
    // exact implementation details
    rowIndex = 0,
    itemName = 'Item',
  } = props;
  const {sectionType} = definition;
  return (
    <div className={css(styles.contents)}>
      {
        // display ghost text if there are no fields
        sectionType === FormSectionType.ITERABLE && !definition.content.length ? (
          <em className={css(styles.itemName)}>
            {itemName} {rowIndex + 1}
          </em>
        ) : (
          definition.content.map((fieldOrSectionDef, index) => {
            if (fieldOrSectionDef.type === 'FormSection') {
              const sectionDef = fieldOrSectionDef;
              return (
                <FormInstanceSection
                  {...props}
                  key={`section-${sectionDef.key}`}
                  definition={sectionDef}
                  autoFocusFirstField={autoFocusFirstField && index === 0}
                />
              );
            } else {
              const fieldDef = fieldOrSectionDef;
              const {key} = fieldDef;
              if (fieldKeyToTableColumnIndex[key]) {
                const [tableKey, tableColumnIndex] = fieldKeyToTableColumnIndex[key];
                const table = tableState[tableKey];
                const computedTable = computedTableState[tableKey];
                const column = table?.columns[tableColumnIndex];
                const {schemas, kind, metadata} = table;
                const columnConstraint = toColumnConstraint({
                  structuredTableColumn: column,
                  schemas,
                  kind,
                  ...metadata,
                });
                const evaluatedCell = computedTable?.[rowIndex]?.[tableColumnIndex];
                return (
                  <FormInstanceTypedValueField
                    key={`field-${key}`}
                    fieldDefinition={fieldDef}
                    columnConstraint={columnConstraint}
                    evaluatedCell={evaluatedCell}
                    updateTypedValue={(typedValue) => {
                      updateTableState(
                        produce(tableState, (draft) => {
                          if (sectionType === FormSectionType.ITERABLE) {
                            const updatedCell = draft[tableKey].rows[rowIndex].cells[tableColumnIndex];
                            updatedCell.typedValue = typedValue;
                            updatedCell.text = '';
                          } else if (sectionType === FormSectionType.STATIC) {
                            // Shared Fields will update every row in the table
                            for (const row of draft[tableKey].rows) {
                              const updatedCell = row.cells[tableColumnIndex];
                              updatedCell.typedValue = typedValue;
                              updatedCell.text = '';
                            }
                          }
                        })
                      );
                    }}
                  />
                );
              }
              return null; // Appeasing the Linter God
            }
          })
        )
      }
    </div>
  );
}
